import { ReactNode, useCallback, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { Label } from './Label';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { InfoPopover } from '../InfoPopover';
import { Spinner } from '../Spinner';

export type SelectProps<T> = {
  label?: string;
  info?: string;
  errorMessage?: string;
  className?: string;
  isFetching?: boolean;
  renderItem: (_item: T) => ReactNode;
  items?: T[];
  onChange: (_item: T | undefined) => void;
  value: T;
};

export const Select = <T extends { id: string }>({
  label,
  info,
  className,
  errorMessage,
  isFetching,
  items,
  renderItem,
  onChange,
  value,
}: SelectProps<T>) => {
  const [isVisible, setIsVisible] = useState(false);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleSelect = (item: T | undefined) => {
    onChange(item);
    setIsVisible(false);
  };

  return (
    <div>
      {label ? (
        <div className="flex items-center mb-2 gap-2">
          <Label className={twJoin('mb-0', !!errorMessage && 'text-red-700 dark:text-red-500')}>{label}</Label>
          {info ? <InfoPopover text={info} /> : null}
        </div>
      ) : null}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsVisible(true)}
          className={twJoin(
            'bg-gray-50 border border-gray-300 text-left text-gray-900 sm:text-sm rounded-lg focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 pr-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500',
            !!errorMessage &&
              'bg-red-50 border-red-500 text-red-900 dark:text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:bg-red-100 dark:border-red-400',
            className,
          )}
        >
          {value ? renderItem(value) : '--'}
        </button>
        {isFetching ? (
          <div className="absolute top-2 right-2">
            <Spinner />
          </div>
        ) : null}
        <div
          ref={ref}
          className={twMerge(
            'z-10 absolute top-12 left-0 right-0 bg-white divide-y divide-gray-100 rounded-lg shadow dark:bg-gray-700 dark:divide-gray-600 overflow-hidden overflow-y-auto max-h-44',
            (!items || !isVisible) && 'hidden',
          )}
        >
          <button
            type="button"
            className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
            onClick={() => handleSelect(undefined)}
          >
            --
          </button>
          {items?.map(item => (
            <button
              type="button"
              key={item.id}
              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
              onClick={() => handleSelect(item)}
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      </div>
      {errorMessage ? <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errorMessage}</p> : null}
    </div>
  );
};
